import { ISubProgramEnrolment, ISubProgramRequirements } from '@carm/_core/program-enrolment/models/sub-program-enrolment.model';
import { IBaseResponse } from '@carm/_utils/models';
import { ISecurityRequirements } from './financial-security-api.model';
import { IHoldHybrisCaseStatus } from '@carm/_core/program-enrolment/models/enrol-program-api.model';
import { PaymentContext } from '@carm/_core/payments/models';

export interface IFinancialSecurityEnrolment extends ISubProgramEnrolment {
    draftId?: string;
    paymentContext?: PaymentContext;
    isBondedApply?: boolean;
    requirements?: IUpdateSecurityRequirements;
}

export interface IUpdateSecurityRequirements extends ISubProgramRequirements {
    modifiedAmount?: number;
    piecesOfEquipmentOperated?: number;
}

export interface IBondInfo {
    dateSubmitted: string;
    status: string;
    statusMsg: string;
    securityDepositId: string;
    suretyProvider: string;
    details: IBondDetails;
}

export interface IBondDetails {
    draftId?: string;
    type: BondTypes;
    category: string;
    number: string;
    bn15: string;
    bondReasonCode: string;
    amount: number;
    suretyProvider: string;
    legislativeAuthority: string;
    validFrom: string;
    validTo: string;
}

export interface ISecurityRequirementBondInfo {
    minimumSecurityRequirement?: number;
    totalSecurityCoverage?: number;
    remaining?: number;
    isRequiredAmountFulfiled?: boolean;
    bonds: ISelectedBondDetail[];
}

export interface ISecurityRequirementInfo extends IBaseResponse {
    securityRequirementAmount: number;
    securityRequirementPostedAmount: number;
    proposedBondAmount: number;
    calculatedSecurityAmount: number;
    rppUtilization: number;
    programType: string; // todo should be type
    programNumber: string;
    programName: string;
    isUpdatable: boolean;
    isRPP: boolean;
    isRequiredAmountFulfiled: boolean;
    isLicenseFeePaid: boolean;
    licenseFeeAmount?: number;
}

export enum BondTypes {
    Cash = 'cash',
    NonCash = 'non_cash'
}

export enum FinancialSecurityRequestSource {
    ProgramRequests = 'program_requests',
    SubprogramRequests = 'program_subprograms',
    AddCashBond = 'add_cash_bond',
    AddNonCashBond = 'add_non_cash_bond',
    SelectBondType = 'select_bond_type',
    FinancialSecurityRequirements = 'financial_security_requirements',
    FinancialSecurityRequirementsAddBonds = 'financial_security_requirements_addbonds',
    FinancialSecurityDashboard = 'financial_security_dashboard',
    FinancialSecuritySuretyDashboard = 'financial_security_surety_dashboard',
    BusinessRegistration = 'business_registration',
    CbsaProgramEnrolments = 'cbsa_program_enrolments',
    TransactionHistory = 'transaction_history',
    ClientTransactionHistory = 'client_transaction_history',
    Payments = 'payments',
    ProgramEnrolmentPendingPayments = 'enrolment_pending_payments',
    ProgramEnrolmentPendingEnrolments = 'enrolment_pending_enrolments',
    ProgramEnrolmentSecurityRequirements = 'enrolment_security_requirements',
    ProgramEnrolmentAddBonds = 'enrolment_add_bonds',
    CashBondCreated = 'cash_bond_created'
}

export interface IFinancialSecurityBond {
    type: BondTypes;
}

export interface IBusinessSecurityRequirement {
    caseNumber: string;
    otherSecurityRequirements?: ISecurityRequirements[];
    rppSecurityRequirements?: ISecurityRequirements[];
    securityRequirementAmount: number;
    securityRequirementPostedAmount: number;
}

export interface ISecurityRequirementUpdateRequest {
    bn15: string;
    amount: number;
}
export interface ISelectedBondDetail extends IHoldHybrisCaseStatus {
    amount: number;
    bn15: string;
    category: string;
    hasLinkedDocuments: boolean;
    id: string;
    linkedDocuments: string[];
    legislativeAuthority: string;
    number: string;
    bondNumber: string;
    bondCategory:string;
    program: string;
    programOData: string;
    securityDepositId: string;
    status: string;
    statusCode: string;
    statusMsg: string;
    submittedDate: string;
    suretyProviderName: string;
    suretyProvider: string;
    clientName: string;
    type: string;
    validFrom: string;
    validTo: string;
    client: string;
    caseNumber: string;
    guid:string;
}

export interface ISuretyBondInfo {
    bonds?: ISuretyBondDetail[];
}

export interface ISuretyBondDetail {
    amount: number;
    bn15: string;
    category: string;
    hasLinkedDocuments: boolean;
    id: string;
    linkedDocuments: string[];
    legislativeAuthority: string;
    number: string;
    program: string;
    programOData: string;
    securityDepositId: string;
    status: string;
    statusCode: string;
    statusMsg: string;
    type: string;
    clientName: string;
    suretyProvider: string;
    suretyProviderName: string;
    submittedDate: string;
    validFrom: string;
    validTo: string;
}

export enum CashBondStatus {
    CREATED = '00',
    REQUESTED_NOT_POSTED = '01',
    REQUESTED = '02',
    PARTIALLY_PAID = '03',
    PAID = '04',
    PARTIALLY_RELEASED = '88',
    PARTIALLY_RETURNED = '89',
    PARTIALLY_PAID_CLEARED = '96',
    RELEASED = '97',
    RETURNED = '98',
    REVERSED = '99'
}

export enum EBondStatus {
    UNDER_GUARANTOR_APPROVAL = '10',
    ACTIVE = '20',
    CANCELLED = '30',
    REJECTED_BY_GUARANTOR = '60',
    WITHDRAWN = 'WITHDRAWN',
    EXPIRED = '81',
    BOND_CLAIM_IN_PROGRESS = '70',
    BOND_CLAIM_COMPLETED = '80',
    SEND_TO_DOJ = '82',
    PARTIALLY_RELEASED = '88',
    ERROR = '00'
}

export enum BondCategory {
    ELECTRONIC = '10' , // 'Electronic Bond',
    PAPER = '20' , // 'Paper Bond',
    GOC = '30' , // 'GoC Bond',
    CERTIFIED = '40' , // 'Certified Cheque',
    MONEY_ORDER = '50' , // 'Money order',
    UNCERTIFIED = '60' , // 'Uncertified Cheque',
    LETTER_CREDIT = '70' // 'Letter of Credit'
}

export enum LegislativeAuthorityCode {
    BRO= 'customs_broker',
    CC = 'carrier_freight_forward',
    WHSE = 'bonded_warehouse',
    // TODO= Validate Custom bonded wareshouse (1012) vs Sufferance warehouse (1013)
    // 'WHSE = '1013',
    DFS = 'duty_free_shop',
    IMP = 'customs_brokers_licencing_regulations'
}

export enum LegislativeAuthorities {
    customs_brokers_licencing_regulations= '01',
    transportation_of_goods_regulations= '02',
    customs_bonded_warehouse_regulations= '03',
    sufferance_bonded_warehouse_regulations= '04',
    duty_free_shop_regulations= '05',
    temporary_importations_regulations= '06',
    accounting_for_imported_goods_and_payment_of_duties_regulations= '07',
    customs_act= '08',
    customs_tariff= '09',
}

export enum BondProgramReasonCode {
    Broker = '1010',
    Carrier = '1011',
    CustomsBondedWarehouse = '1012',
    SufferanceWarehouse = '1013',
    DutyFreeShop = '1014',
    Importer = '1015'
}
